/* RESPONSIBLE TEAM: team-fin-voice */
import type IntlService from 'embercom/services/intl';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type PhoneNumber from 'embercom/models/calling-phone-number';
import {
  DEFAULT_VOICE,
  FIN_VOICE_11LABS_VOICES,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import {
  DEFAULT_VOICE_LANGUAGE,
  FIN_VOICE_VOICE_LANGUAGE_OPTIONS,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';

/**
 * Finds the first configured voice from existing inbound number configurations.
 * Falls back to DEFAULT_VOICE ('Hope') if no inbound lines have a voice set.
 */
export function getInboundDefaultVoice(
  numberConfigurations: Record<string, FinVoiceNumberConfiguration>,
): string {
  for (let id of Object.keys(numberConfigurations)) {
    let voice = numberConfigurations[id]?.voice;
    if (voice) {
      return voice;
    }
  }
  return DEFAULT_VOICE;
}

/**
 * Builds a "Language • Voice" label for a given phone number and its configuration.
 *
 * @param voice - Preferred voice name. Falls back to the number configuration's voice,
 *   then to the first configured inbound voice, then to DEFAULT_VOICE ('Hope').
 */
export function buildVoiceLabel(
  phoneNumber: PhoneNumber | undefined,
  numberConfiguration: FinVoiceNumberConfiguration | undefined,
  numberConfigurations: Record<string, FinVoiceNumberConfiguration>,
  intl: IntlService,
  voice?: string | null,
): string {
  let savedLanguage = phoneNumber?.locale || DEFAULT_VOICE_LANGUAGE;
  let languageData = FIN_VOICE_VOICE_LANGUAGE_OPTIONS.find(
    (language) => language.value === savedLanguage,
  );
  let languageText = languageData?.text ? intl.t(languageData.text) : '';

  let resolvedVoice =
    voice || numberConfiguration?.voice || getInboundDefaultVoice(numberConfigurations);
  let voiceText = FIN_VOICE_11LABS_VOICES.find((v) => v.value === resolvedVoice)?.text || '';

  return [languageText, voiceText].filter(Boolean).join(' \u2022 ');
}
